import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const GalleryImage = ({ image, index, onClick }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const isVideo = image.type === 'video' || 
                  image.src.toLowerCase().endsWith('.mp4') || 
                  image.src.toLowerCase().endsWith('.mov');

  // Intersection Observer to handle lazy-loading and autoplay/pause
  useEffect(() => {
    if (!isVideo) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        rootMargin: '200px', // Start loading slightly before it enters view
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isVideo]);

  // Manage video playback state
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    if (isInView) {
      // Lazy-assign src only when near viewport
      if (!videoRef.current.src) {
        videoRef.current.src = image.src;
        videoRef.current.load();
      }
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented (unmuted or user interaction required)
          // We stay muted so this should rarely happen
        });
      }
    } else {
      if (videoRef.current.src) {
        videoRef.current.pause();
      }
    }
  }, [isInView, isVideo, image.src]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
      className="relative mb-6 break-inside-avoid group cursor-zoom-in"
      onClick={() => onClick(index)}
    >
      <div className="relative w-full overflow-hidden bg-brand-espresso/5 shadow-sm transition-all duration-700 group-hover:shadow-xl ring-1 ring-brand-espresso/10">
        
        {isVideo ? (
          <div className="relative w-full h-auto">
            {/* Video Element - Optimized for Autoplay */}
            <video 
              ref={videoRef}
              poster={image.poster}
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => setIsLoaded(true)}
              className={`w-full h-auto block transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            />

            {/* Poster/Placeholder layer (shown while video loads) */}
            {!isLoaded && (
              <div className="absolute inset-0 z-0">
                {image.poster ? (
                  <img 
                    src={image.poster} 
                    alt={image.alt}
                    className="w-full h-auto block"
                  />
                ) : (
                  <div className="w-full aspect-[4/5] bg-brand-ivory flex flex-col items-center justify-center">
                    <SafeIcon name="PlayCircle" className="w-8 h-8 text-brand-espresso/10" />
                  </div>
                )}
              </div>
            )}

            {/* Subtle indicator that this is a video */}
            <div className="absolute top-4 right-4 z-10 text-white/40 group-hover:text-white/80 transition-colors">
              <SafeIcon name="Play" className="w-3 h-3" />
            </div>
          </div>
        ) : (
          <img 
            src={image.src} 
            alt={image.alt || 'Padua Weddings'} 
            loading="lazy"
            decoding="async"
            className="w-full h-auto block transition-transform duration-[1.5s] ease-out group-hover:scale-[1.02]"
          />
        )}

        {/* Subtle dark tint on hover */}
        <div className="absolute inset-0 bg-brand-espresso/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </div>
    </motion.div>
  );
};

export default GalleryImage;